import { AppointmentDetail, ViewportOverlay, useToast, type Appointment } from '@elva/ui';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { extractApiErrorMessage } from '@/api/errors';
import { ElvaPageLoader } from '@/components/ElvaPageLoader';
import { useLocationId } from '@/data/useLocationId';
import { resolveAvatarUrl } from '@/features/patients/adapters/patientAdapters';
import { usePatientProfile } from '@/features/patients/hooks/usePatientProfile';
import { patientProfilePath } from '@/features/patients/utils/patientProfileRoutes';
import { useBootstrap } from '@/features/workspace/hooks/useBootstrap';
import { adaptAppointmentDetail } from '../adapters/appointmentDetailAdapters';
import type { AppointmentStatusAction } from '../api/types';
import {
  useAppointmentDetail,
  useChangeAppointmentOperatory,
  useChangeAppointmentStatus,
  useCheckEligibility,
  useResendKioskCode,
} from '../hooks/useAppointmentDetail';

export interface AppointmentDetailConnectedProps {
  appointment: Appointment | null;
  onClose: () => void;
}

export function AppointmentDetailConnected({
  appointment,
  onClose,
}: AppointmentDetailConnectedProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const locationId = useLocationId();
  const appointmentId = appointment ? Number(appointment.id) : null;

  const { data: detail, isLoading, isError } = useAppointmentDetail(appointmentId);
  const { data: bootstrap } = useBootstrap(locationId);

  const changeStatus = useChangeAppointmentStatus(appointmentId ?? 0);
  const changeOperatory = useChangeAppointmentOperatory(appointmentId ?? 0);
  const resendKioskCode = useResendKioskCode(appointmentId ?? 0);
  const checkEligibility = useCheckEligibility(appointmentId ?? 0);

  const operatoryOptions = useMemo(() => bootstrap?.operatories ?? [], [bootstrap]);

  const adapted = useMemo(() => {
    if (!detail) return undefined;
    const operatoryDisplayName = operatoryOptions.find(
      (op) => op.id === detail.operatory?.id,
    )?.display_name;
    return adaptAppointmentDetail(detail, { operatoryDisplayName });
  }, [detail, operatoryOptions]);

  const patientProfileHref = adapted ? patientProfilePath(adapted.patientId) : undefined;

  // The photo lives on the patient profile, not on /detail — don't block the modal on it.
  const { data: patientProfile } = usePatientProfile(adapted ? Number(adapted.patientId) : null);
  const patientAvatarUrl = patientProfile ? resolveAvatarUrl(patientProfile) : undefined;
  const patientInitials = patientProfile?.initials?.trim() || undefined;

  const handleViewProfile = useCallback(() => {
    if (!patientProfileHref) return;
    onClose();
    navigate(patientProfileHref);
  }, [navigate, onClose, patientProfileHref]);

  const handleChangeStatus = useCallback(
    (status: string) => {
      if (status === adapted?.status) return;
      changeStatus.mutate(status as AppointmentStatusAction, {
        onError: (err: unknown) => {
          toast({
            title: 'Couldn’t update status',
            description: extractApiErrorMessage(err),
            variant: 'error',
          });
        },
      });
    },
    [adapted?.status, changeStatus, toast],
  );

  const handleChangeOperatory = useCallback(
    (displayName: string) => {
      const target = operatoryOptions.find((op) => op.display_name === displayName);
      if (!target || target.id === adapted?.operatoryId) return;
      changeOperatory.mutate(target.id, {
        onError: (err: unknown) => {
          toast({
            title: 'Couldn’t change operatory',
            description: extractApiErrorMessage(err),
            variant: 'error',
          });
        },
      });
    },
    [adapted?.operatoryId, changeOperatory, operatoryOptions, toast],
  );

  const handleResendKioskCode = useCallback(() => {
    resendKioskCode.mutate(undefined, {
      onSuccess: ({ channel }) => {
        toast({ title: `Kiosk code sent by ${channel}`, variant: 'success' });
      },
      onError: (err: unknown) => {
        toast({
          title: 'Couldn’t resend kiosk code',
          description: extractApiErrorMessage(err),
          variant: 'error',
        });
      },
    });
  }, [resendKioskCode, toast]);

  const handleCheckEligibility = useCallback(() => {
    checkEligibility.mutate(undefined, {
      onError: (err: unknown) => {
        toast({
          title: 'Couldn’t check eligibility',
          description: extractApiErrorMessage(err),
          variant: 'error',
        });
      },
    });
  }, [checkEligibility, toast]);

  const busyAction = changeStatus.isPending
    ? 'status'
    : changeOperatory.isPending
      ? 'operatory'
      : resendKioskCode.isPending
        ? 'kiosk'
        : checkEligibility.isPending
          ? 'eligibility'
          : null;

  if (!appointment) return null;

  if (isLoading) {
    return (
      <ViewportOverlay open onClose={onClose}>
        <div className="flex min-h-[240px] min-w-[320px] items-center justify-center rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-0)] p-12 shadow-[var(--sh-3)]">
          <ElvaPageLoader />
        </div>
      </ViewportOverlay>
    );
  }

  if (isError || !adapted) {
    return (
      <ViewportOverlay open onClose={onClose}>
        <div className="flex min-h-[240px] min-w-[320px] flex-col items-center justify-center gap-3 rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-0)] p-12 shadow-[var(--sh-3)]">
          <p className="text-[14px] font-medium text-[var(--ink-0)]">Something went wrong</p>
          <p className="text-[12px] text-[var(--ink-3)]">Could not load appointment details.</p>
        </div>
      </ViewportOverlay>
    );
  }

  return (
    <AppointmentDetail
      appointment={adapted.appointment}
      onClose={onClose}
      dossier={adapted.dossier}
      insurance={adapted.insurance}
      providerName={adapted.providerName}
      {...(patientAvatarUrl ? { patientAvatarUrl } : {})}
      {...(patientInitials ? { patientInitials } : {})}
      currentStatus={adapted.status}
      operatories={operatoryOptions.map((op) => op.display_name)}
      busyAction={busyAction}
      eligibilityStatus={adapted.eligibility.status}
      eligibilityStale={adapted.eligibility.stale}
      {...(adapted.eligibility.lastChecked
        ? { eligibilityLastChecked: adapted.eligibility.lastChecked }
        : {})}
      {...(patientProfileHref ? { patientProfileHref, onViewProfile: handleViewProfile } : {})}
      onChangeStatus={handleChangeStatus}
      onChangeOperatory={handleChangeOperatory}
      onResendKioskCode={handleResendKioskCode}
      onCheckEligibility={handleCheckEligibility}
    />
  );
}
